import axios from '../config/axios';
import { getApiUrl } from '../config/environment';
import type {
  DentalChart,
  DentalChartEntry,
  DentalChartEntryPayload,
  DentalChartResponse,
  DentalImplant,
  EndodonticTreatment,
  OrthodonticTreatment,
  PaginatedResponse,
  PatientOdontologySummary,
  PeriodontalChart,
} from '../types/odontology';

const BASE = '/clinic/odontology';

// ── Odontograma ──────────────────────────────────────────────────────────────

export async function getDentalChart(patientId: string, unitId?: string | null): Promise<DentalChartResponse> {
  const res = await axios.get(getApiUrl(`${BASE}/chart`), {
    params: { patient_id: patientId, unit_id: unitId || undefined },
  });
  return res.data?.data;
}

export async function updateDentalChart(id: string, payload: Partial<DentalChart>): Promise<DentalChart> {
  const res = await axios.put(getApiUrl(`${BASE}/chart/${id}`), payload);
  return res.data?.data;
}

export async function listDentalChartEntries(
  params: Record<string, unknown> = {},
): Promise<PaginatedResponse<DentalChartEntry>> {
  const res = await axios.get(getApiUrl(`${BASE}/chart-entries`), { params });
  return res.data;
}

export async function createDentalChartEntry(payload: DentalChartEntryPayload): Promise<DentalChartEntry> {
  const res = await axios.post(getApiUrl(`${BASE}/chart-entries`), payload);
  return res.data?.data;
}

export async function updateDentalChartEntry(
  id: string,
  payload: DentalChartEntryPayload,
): Promise<DentalChartEntry> {
  const res = await axios.put(getApiUrl(`${BASE}/chart-entries/${id}`), payload);
  return res.data?.data;
}

export async function deleteDentalChartEntry(id: string): Promise<void> {
  await axios.delete(getApiUrl(`${BASE}/chart-entries/${id}`));
}

// ── Periodontograma ──────────────────────────────────────────────────────────

export async function listPeriodontalCharts(
  params: Record<string, unknown> = {},
): Promise<PaginatedResponse<PeriodontalChart>> {
  const res = await axios.get(getApiUrl(`${BASE}/periodontal-charts`), { params });
  return res.data;
}

export async function getPeriodontalChart(id: string): Promise<PeriodontalChart> {
  const res = await axios.get(getApiUrl(`${BASE}/periodontal-charts/${id}`));
  return res.data?.data;
}

export async function getPeriodontalChartPrintData(id: string) {
  const res = await axios.get(getApiUrl(`${BASE}/periodontal-charts/${id}/print`));
  return res.data?.data;
}

export async function createPeriodontalChart(payload: Partial<PeriodontalChart>): Promise<PeriodontalChart> {
  const res = await axios.post(getApiUrl(`${BASE}/periodontal-charts`), payload);
  return res.data?.data;
}

export async function updatePeriodontalChart(
  id: string,
  payload: Partial<PeriodontalChart>,
): Promise<PeriodontalChart> {
  const res = await axios.put(getApiUrl(`${BASE}/periodontal-charts/${id}`), payload);
  return res.data?.data;
}

export async function deletePeriodontalChart(id: string): Promise<void> {
  await axios.delete(getApiUrl(`${BASE}/periodontal-charts/${id}`));
}

// ── Endodontia ───────────────────────────────────────────────────────────────

export async function listEndodonticTreatments(
  params: Record<string, unknown> = {},
): Promise<PaginatedResponse<EndodonticTreatment>> {
  const res = await axios.get(getApiUrl(`${BASE}/endodontic-treatments`), { params });
  return res.data;
}

export async function createEndodonticTreatment(
  payload: Partial<EndodonticTreatment>,
): Promise<EndodonticTreatment> {
  const res = await axios.post(getApiUrl(`${BASE}/endodontic-treatments`), payload);
  return res.data?.data;
}

export async function updateEndodonticTreatment(
  id: string,
  payload: Partial<EndodonticTreatment>,
): Promise<EndodonticTreatment> {
  const res = await axios.put(getApiUrl(`${BASE}/endodontic-treatments/${id}`), payload);
  return res.data?.data;
}

export async function deleteEndodonticTreatment(id: string): Promise<void> {
  await axios.delete(getApiUrl(`${BASE}/endodontic-treatments/${id}`));
}

// ── Implantes ────────────────────────────────────────────────────────────────

export async function listDentalImplants(
  params: Record<string, unknown> = {},
): Promise<PaginatedResponse<DentalImplant>> {
  const res = await axios.get(getApiUrl(`${BASE}/implants`), { params });
  return res.data;
}

export async function createDentalImplant(payload: Partial<DentalImplant>): Promise<DentalImplant> {
  const res = await axios.post(getApiUrl(`${BASE}/implants`), payload);
  return res.data?.data;
}

export async function updateDentalImplant(
  id: string,
  payload: Partial<DentalImplant>,
): Promise<DentalImplant> {
  const res = await axios.put(getApiUrl(`${BASE}/implants/${id}`), payload);
  return res.data?.data;
}

export async function deleteDentalImplant(id: string): Promise<void> {
  await axios.delete(getApiUrl(`${BASE}/implants/${id}`));
}

// ── Ortodontia ───────────────────────────────────────────────────────────────

export async function listOrthodonticTreatments(
  params: Record<string, unknown> = {},
): Promise<PaginatedResponse<OrthodonticTreatment>> {
  const res = await axios.get(getApiUrl(`${BASE}/orthodontic-treatments`), { params });
  return res.data;
}

export async function createOrthodonticTreatment(
  payload: Partial<OrthodonticTreatment>,
): Promise<OrthodonticTreatment> {
  const res = await axios.post(getApiUrl(`${BASE}/orthodontic-treatments`), payload);
  return res.data?.data;
}

export async function updateOrthodonticTreatment(
  id: string,
  payload: Partial<OrthodonticTreatment>,
): Promise<OrthodonticTreatment> {
  const res = await axios.put(getApiUrl(`${BASE}/orthodontic-treatments/${id}`), payload);
  return res.data?.data;
}

export async function deleteOrthodonticTreatment(id: string): Promise<void> {
  await axios.delete(getApiUrl(`${BASE}/orthodontic-treatments/${id}`));
}

// ── Resumo ───────────────────────────────────────────────────────────────────

export async function getPatientOdontologySummary(
  patientId: string,
  unitId?: string | null,
): Promise<PatientOdontologySummary> {
  const res = await axios.get(getApiUrl(`${BASE}/summary`), {
    params: { patient_id: patientId, unit_id: unitId || undefined },
  });
  return res.data?.data;
}
